"use client";

import { useState } from "react";
import { Info, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Disclaimer } from "@/components/disclaimer";
import { TickerCombobox } from "@/components/portfolio/ticker-combobox";
import { computeVorabpauschale, FUND_TYPE_LABELS, type FundType } from "@/lib/tax/vorabpauschale";
import { VORABPAUSCHALE } from "@/lib/config";
import { formatCurrency, formatPercent } from "@/lib/format";
import { cn } from "@/lib/utils";

interface DepotPositionOption {
  symbol: string;
  name: string;
  currentValue: number;
  currency: string;
}

interface Row {
  id: string;
  label: string;
  currency: string;
  isAccumulating: boolean;
  fundType: FundType;
  valueStartOfYear: string;
  valueEndOfYear: string;
  distributions: string;
  monthsHeld: number;
}

function newRow(): Row {
  return {
    id: crypto.randomUUID(),
    label: "",
    currency: "EUR",
    isAccumulating: true,
    fundType: "equity",
    valueStartOfYear: "",
    valueEndOfYear: "",
    distributions: "0",
    monthsHeld: 12,
  };
}

function parseAmount(draft: string): number {
  const n = Number(draft.replace(",", "."));
  return Number.isFinite(n) ? n : 0;
}

/**
 * Vorabpauschale-Rechner (backlog.md -> Punkt 12) - mechanischer Rechner fuer die
 * deutsche Vorabpauschale bei thesaurierenden/teil-thesaurierenden Fonds/ETFs im Depot.
 * Bewusst ein manuelles "ist (teil-)thesaurierender Fonds"-Haekchen pro Zeile statt einer
 * geratenen Auto-Erkennung aus dem Ticker (Fondseigenschaft ist aus einem Symbol allein
 * nicht zuverlaessig ableitbar). Eigenstaendiger Rechner (kein neues DB-Schema/keine
 * Migration) - Positionen aus dem echten Depot koennen als Startpunkt fuer Bezeichnung
 * und aktuellen Wert uebernommen werden, Jahresanfangswert/Ausschuettungen/Fondstyp
 * bleiben manuelle Eingaben, da im Projekt weder Jahresanfangskurse je Fondsposition noch
 * eine Thesaurierungs-/Fondstyp-Kennzeichnung vorgehalten werden.
 */
export function VorabpauschaleCalculator({ positions }: { positions: DepotPositionOption[] }) {
  const [rows, setRows] = useState<Row[]>([newRow()]);

  function updateRow(id: string, patch: Partial<Row>) {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  }

  function removeRow(id: string) {
    setRows((prev) => (prev.length > 1 ? prev.filter((r) => r.id !== id) : prev));
  }

  function addRow() {
    setRows((prev) => [...prev, newRow()]);
  }

  function applyPosition(id: string, symbol: string) {
    const position = positions.find((p) => p.symbol === symbol);
    if (!position) return;
    updateRow(id, {
      label: `${position.symbol} – ${position.name}`,
      currency: position.currency,
      valueEndOfYear: String(position.currentValue.toFixed(2)),
    });
  }

  const results = rows.map((row) => ({
    row,
    result: row.isAccumulating
      ? computeVorabpauschale({
          valueStartOfYear: parseAmount(row.valueStartOfYear),
          valueEndOfYear: parseAmount(row.valueEndOfYear),
          distributions: parseAmount(row.distributions),
          basiszins: VORABPAUSCHALE.basiszins,
          fundType: row.fundType,
          monthsHeld: row.monthsHeld,
        })
      : undefined,
  }));

  const totalsByCurrency = new Map<string, { vorabpauschale: number; estimatedTax: number }>();
  for (const { row, result } of results) {
    if (!result) continue;
    const t = totalsByCurrency.get(row.currency) ?? { vorabpauschale: 0, estimatedTax: 0 };
    t.vorabpauschale += result.vorabpauschale;
    t.estimatedTax += result.estimatedTax;
    totalsByCurrency.set(row.currency, t);
  }

  return (
    <div className="flex flex-col gap-4 rounded-lg border border-hairline p-4">
      <div>
        <span className="inline-flex items-center gap-1.5 font-serif text-lg font-semibold text-ink">
          Vorabpauschale-Rechner
          <Tooltip>
            <TooltipTrigger asChild>
              <button type="button" aria-label="Erklärung: Vorabpauschale" className="text-muted-foreground hover:text-ink">
                <Info className="h-3.5 w-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent className="max-w-72">
              Thesaurierende (nicht ausschüttende) Fonds/ETFs unterliegen jährlich der
              Vorabpauschale (§ 18 InvStG): Fondswert Jahresanfang × Basiszins × 0,7,
              gedeckelt auf die tatsächliche Wertsteigerung im Jahr, abzüglich bereits
              ausgeschütteter Beträge.
            </TooltipContent>
          </Tooltip>
        </span>
        <p className="mt-1 text-xs text-ink-secondary">
          Basiszins {formatPercent(VORABPAUSCHALE.basiszins)} (BMF-Schreiben vom 13.01.2026
          für das Steuerjahr 2026). Mechanischer Rechner, ersetzt keine steuerliche
          Beratung.
        </p>
      </div>

      <div className="flex flex-col gap-4">
        {rows.map((row) => {
          const entry = results.find((r) => r.row.id === row.id)!;
          return (
            <div key={row.id} className="flex flex-col gap-2.5 rounded-md border border-hairline p-3">
              <div className="flex flex-wrap items-center gap-2">
                <div className="min-w-48 flex-1">
                  <TickerCombobox
                    value={row.label}
                    onQueryChange={(q) => updateRow(row.id, { label: q })}
                    onSelect={(ref) => updateRow(row.id, { label: `${ref.symbol} – ${ref.name}` })}
                    placeholder="Bezeichnung (z. B. MSCI World ETF) - Ticker/ISIN durchsuchbar"
                  />
                </div>
                {positions.length > 0 && (
                  <select
                    defaultValue=""
                    onChange={(e) => {
                      if (e.target.value) applyPosition(row.id, e.target.value);
                      e.target.value = "";
                    }}
                    className="rounded-md border border-hairline bg-card px-2 py-1 text-xs text-ink"
                    aria-label="Aus Depot übernehmen"
                  >
                    <option value="">Aus Depot übernehmen…</option>
                    {positions.map((p) => (
                      <option key={p.symbol} value={p.symbol}>
                        {p.symbol} – {p.name}
                      </option>
                    ))}
                  </select>
                )}
                {rows.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeRow(row.id)}
                    aria-label="Zeile entfernen"
                    className="ml-auto text-muted-foreground hover:text-negative"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>

              <label className="flex items-center gap-1.5 text-xs text-ink-secondary">
                <input
                  type="checkbox"
                  checked={row.isAccumulating}
                  onChange={(e) => updateRow(row.id, { isAccumulating: e.target.checked })}
                />
                Fonds ist (teil-)thesaurierend
              </label>

              {!row.isAccumulating ? (
                <p className="text-xs text-muted-foreground">
                  Vorabpauschale betrifft nur (teil-)thesaurierende Fonds/ETFs – bei diesem
                  Fonds ohne Haken keine Berechnung.
                </p>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
                    <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                      Fondstyp
                      <select
                        value={row.fundType}
                        onChange={(e) => updateRow(row.id, { fundType: e.target.value as FundType })}
                        className="rounded-md border border-hairline bg-card px-2 py-1 text-sm text-ink"
                      >
                        {(Object.keys(FUND_TYPE_LABELS) as FundType[]).map((key) => (
                          <option key={key} value={key}>
                            {FUND_TYPE_LABELS[key]}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                      Volle Monate gehalten
                      <select
                        value={row.monthsHeld}
                        onChange={(e) => updateRow(row.id, { monthsHeld: Number(e.target.value) })}
                        className="rounded-md border border-hairline bg-card px-2 py-1 text-sm text-ink"
                      >
                        {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                          <option key={m} value={m}>
                            {m}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                      Währung
                      <Input
                        value={row.currency}
                        onChange={(e) => updateRow(row.id, { currency: e.target.value.toUpperCase().slice(0, 3) })}
                        className="bg-card"
                      />
                    </label>
                    <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                      Wert Jahresanfang
                      <Input
                        type="number"
                        min={0}
                        value={row.valueStartOfYear}
                        onChange={(e) => updateRow(row.id, { valueStartOfYear: e.target.value })}
                        className="bg-card"
                      />
                    </label>
                    <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                      Wert Jahresende (bzw. heute)
                      <Input
                        type="number"
                        min={0}
                        value={row.valueEndOfYear}
                        onChange={(e) => updateRow(row.id, { valueEndOfYear: e.target.value })}
                        className="bg-card"
                      />
                    </label>
                    <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                      Ausschüttungen im Jahr
                      <Input
                        type="number"
                        min={0}
                        value={row.distributions}
                        onChange={(e) => updateRow(row.id, { distributions: e.target.value })}
                        className="bg-card"
                      />
                    </label>
                  </div>

                  {entry.result && (
                    <div className="mt-1 flex flex-wrap gap-x-6 gap-y-1 border-t border-hairline pt-2 text-sm">
                      <span className="text-ink-secondary">
                        Vorabpauschale:{" "}
                        <span className="font-tabular font-medium text-ink">
                          {formatCurrency(entry.result.vorabpauschale, row.currency)}
                        </span>
                      </span>
                      <span className="text-ink-secondary">
                        Steuerpflichtig ({formatPercent(1 - entry.result.partialExemptionRate, 0)} nach
                        Teilfreistellung):{" "}
                        <span className="font-tabular font-medium text-ink">
                          {formatCurrency(entry.result.taxableAmount, row.currency)}
                        </span>
                      </span>
                      <span className="text-ink-secondary">
                        Geschätzte Steuer:{" "}
                        <span className="font-tabular font-medium text-ink">
                          {formatCurrency(entry.result.estimatedTax, row.currency)}
                        </span>
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>

      <Button type="button" variant="outline" size="sm" onClick={addRow} className="w-fit">
        + Position hinzufügen
      </Button>

      {totalsByCurrency.size > 0 && (
        <div className={cn("flex flex-col gap-1.5 border-t border-hairline pt-3 text-sm", rows.length < 2 && "hidden")}>
          <span className="text-xs font-medium text-ink-secondary">Summe über alle Positionen</span>
          {[...totalsByCurrency.entries()].map(([currency, t]) => (
            <div key={currency} className="flex items-center gap-4">
              <span className="text-ink-secondary">{currency}:</span>
              <span className="font-tabular text-ink">Vorabpauschale {formatCurrency(t.vorabpauschale, currency)}</span>
              <span className="font-tabular text-ink">geschätzte Steuer {formatCurrency(t.estimatedTax, currency)}</span>
            </div>
          ))}
        </div>
      )}

      <p className="text-xs leading-relaxed text-ink-secondary">
        Grobe Schätzung mit Abgeltungsteuer + Solidaritätszuschlag ({formatPercent(VORABPAUSCHALE.flatTaxRate)}{" "}
        effektiv), <strong>ohne</strong> Kirchensteuer und <strong>ohne</strong> deinen
        Sparerpauschbetrag (gilt personen-/depotübergreifend, hier nicht bekannt) - die
        tatsächliche Steuerlast kann dadurch niedriger ausfallen. Kein Ersatz für
        steuerliche Beratung.
      </p>
      <Disclaimer />
    </div>
  );
}
